import { setNestedObjectValues } from "formik";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import { testId } from "./testId";
import { v4 as uuid } from "uuid";
import { formatDouble, formatFilter } from "./tableUtils";

export const useInput = (ele) => {
  const [value, setValue] = useState(ele);
  return {
    value,
    reset: () => setValue(""),
    setValue: (val) => setValue(val),
    bind: {
      value,
      onChange: (event) => {
        setValue(event.target.value);
      },
    },
  };
};

export const useInputWithFunc = (ele, callBackfunct) => {
  const [value, setValue] = useState(ele);
  return {
    value,
    reset: () => setValue(""),
    setValue: (val) => {
      setValue(val);
      callBackfunct(val);
    },
    bind: {
      value,
      onChange: (event) => {
        setValue(event.target.value);
        callBackfunct(event.target.value);
      },
    },
  };
};

export function ValidateEmail(mail) {
  if (/^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/.test(mail)) {
    return true;
  }
  return false;
}

export function CheckPassword(inputtxt) {
  var passw = /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{6,20}$/;
  if (inputtxt.match(passw)) {
    return true;
  } else {
    return false;
  }
}

export function stringLength(input, length) {
  if (input.length >= length) {
    return true;
  } else {
    return false;
  }
}

export const handleLoading = async () => {
  await Swal.fire({
    title: "Wait ...",
    onBeforeOpen() {
      Swal.showLoading();
    },
    showConfirmButton: false,
    allowOutsideClick: false,
    allowEscapeKey: false,
    allowEnterKey: false,
  });
};
export const handleSucces = async (successText, title) => {
  await Swal.fire({
    toast: true,
    position: "top-end",
    timer: 2000,
    timerProgressBar: true,
    showConfirmButton: false,
    icon: "success",
    width: 300,
    height: 200,
    title: title,
    text: successText,
    showClass: {
      popup: "",
    },
    hideClass: {
      popup: "",
    },
  }).then(() => {
    return Promise.resolve("success");
  });
};

/**
 * Stamp a Swal dialog with stable test ids.
 *
 * SweetAlert renders its own markup, so a test has nothing to hold on to but CSS classes -- and
 * the e2e suite bans those, because a class name is a rendering detail that changes when the
 * library is upgraded and takes every spec with it. This gives each dialog a name instead.
 *
 * Attribute-setting only. It runs in `onOpen` -- sweetalert2 is v9 here, where the hook is
 * `onOpen`; `didOpen` is the v10 name and is silently ignored on v9, which is exactly how
 * this shipped once with no test ids and no error. It changes no copy, no flow and no `preConfirm`,
 * and a dialog that is never asserted on simply carries an unread attribute.
 */
export const dialogTestIds = (name) => (popup) => {
  if (!popup || !popup.setAttribute) return;
  popup.setAttribute("data-testid", `dialog-${name}`);
  const stamp = (selector, suffix) => {
    const el = popup.querySelector(selector);
    if (el) el.setAttribute("data-testid", `dialog-${name}-${suffix}`);
    return !!el;
  };
  stamp(".swal2-confirm", "confirm");
  stamp(".swal2-cancel", "cancel");
  stamp(".swal2-deny", "deny");
  stamp(".swal2-input", "input");
  /*
   * The text node, under whichever name this version of sweetalert2 gives it -- v10 renamed it
   * to .swal2-html-container, v9 (what is installed) calls it .swal2-content.
   *
   * The FIRST one that exists, not both. In v9 both selectors match: .swal2-html-container is
   * nested inside #swal2-content, so stamping each put one id on two elements and every
   * `getByTestId("dialog-<name>-body")` in the suite resolved to two -- a strict-mode violation
   * on what looks like the most ordinary assertion there is. The inner node is the one that
   * holds only the message under either version, so it is tried first.
   */
  stamp(".swal2-html-container", "body") || stamp(".swal2-content", "body");
};

export const handleInfoToast = async (text, title = "") => {
  await Swal.fire({
    toast: true,
    position: "top-end",
    timer: 2000,
    timerProgressBar: true,
    showConfirmButton: false,
    icon: "info",
    width: 300,
    height: 200,
    title: title,
    text: text,
    showClass: {
      popup: "",
    },
    hideClass: {
      popup: "",
    },
    onOpen: dialogTestIds("info-toast"),
  }).then(() => {
    return Promise.resolve("success");
  });
};

export const handleInfoSuccess = (
  messageText,
  title = "Success",
  icon = "success"
) => {
  return Swal.fire({
    title: title,
    text: messageText,
    icon: icon,
    customClass: {
      icon: "swal2-icon-default",
    },
    onOpen: dialogTestIds("info-success"),
  });
};

export const handleInfo = (messageText, title = "Empty", icon = "info") => {
  return Swal.fire({
    text: messageText,
    onOpen: dialogTestIds("info"),
  });
};

export const handleDelete = async (messageText, action) => {
  await Swal.fire({
    icon: "warning",
    title: "Warning",
    text: messageText,
    showDenyButton: true,
    showCancelButton: true,
    confirmButtonText: `Confirm`,
    denyButtonText: `Cancel`,
  }).then(async (result) => {
    if (result.isConfirmed) {
      await action();
      return Promise.resolve("success");
    }
    return Promise.reject("cancel");
  });
};

export const handleInput = async (
  messageText,
  icon = "warning",
  title = "Need some more data",
  type = "text"
) => {
  return await Swal.fire({
    icon: icon,
    title: title,
    text: messageText,
    input: type,
    showLoaderOnConfirm: true,
    showDenyButton: true,
    showCancelButton: true,
    confirmButtonText: `Confirm`,
    denyButtonText: `Cancel`,
  }).then((result) => {
    if (result.isConfirmed) {
      return Promise.resolve(result?.value);
    }
    return Promise.reject("cancel");
  });
};

export const safeQueryBuilder = (query, params) => {
  if (!!!params) return query;
  for (var key in params) {
    if (!query.includes("?") && params[key]) {
      query = query + "?" + key + "=" + params[key];
    } else if (params[key]) {
      query = query + "&" + key + "=" + params[key];
    }
  }
  return query;
};

export const searchQueryBuilder = (query, params) => {
  if (!!!params) return query;
  params.map((data, index) => {
    if (!query.includes("search") && data.value && data.operation) {
      var search = "&search=";
      if (!query.includes("?")) {
        search = "?search=";
      }
      query =
        query + search + data.key + data.operation + formatFilter(data.value);
    } else if (data.value && data.operation) {
      query =
        query + "," + data.key + data.operation + formatFilter(data.value);
    }
  });

  return query;
};

export const searchBuilder = (data) =>
  data?.key + data?.operation + data?.value;

export const downloadAsCsv = (content, fileName, mimeType) => {
  var a = document.createElement("a");
  mimeType = mimeType || "application/octet-stream";

  if (navigator.msSaveBlob) {
    // IE10
    navigator.msSaveBlob(
      new Blob([content], {
        type: mimeType,
      }),
      fileName
    );
  } else if (URL && "download" in a) {
    //html5 A[download]
    a.href = URL.createObjectURL(
      new Blob([content], {
        type: mimeType,
      })
    );
    a.setAttribute("download", fileName);
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  } else {
    window.location.href =
      "data:application/octet-stream," + encodeURIComponent(content); // only this mime type is supported
  }
};

export const ColoredLine = ({ color }) => (
  <hr
    style={{
      color: color,
      backgroundColor: color,
      printColorAdjust: "exact",
    }}
  />
);

/**
 * Every message in a Formik `errors` tree, including the nested ones.
 *
 * Errors mirror the shape of the values, so a bad field inside `partner` or inside a product
 * row is an object or an array, not a string -- flattening by hand is what makes the dialog
 * below able to name a field the user cannot see.
 */
export const collectErrorMessages = (errors) =>
  Object.values(errors ?? {}).flatMap((value) => {
    if (typeof value === "string") return value.trim() ? [value] : [];
    if (value && typeof value === "object") return collectErrorMessages(value);
    return [];
  });

/**
 * Save, or say why not.
 *
 * Formik abandons a submit when validation fails and reports it only through `errors` -- which
 * is invisible for any control the screen keeps collapsed or does not render at all. The Save
 * button then looks dead: no dialog, no request, no console line, nothing to tell a user their
 * work was not saved. This names the blocking fields and marks them touched, so the inline
 * message is already there when the section holding it is opened.
 *
 * `beforeSubmit` runs synchronously immediately before `handleSubmit`, which preserves each
 * call site's existing (set a flag, then submit) ordering rather than quietly changing which
 * values the submit sees.
 *
 * Returns whether the submit was handed to Formik.
 */
export const submitOrExplainErrors = async ({
  validateForm,
  setTouched,
  handleSubmit,
  beforeSubmit,
  t = (text) => text,
}) => {
  const errors = (await validateForm()) ?? {};
  const messages = collectErrorMessages(errors);

  if (messages.length === 0) {
    if (beforeSubmit) beforeSubmit();
    handleSubmit();
    return true;
  }

  // `false` -- re-validating here would only recompute what we already hold.
  if (setTouched) setTouched(setNestedObjectValues(errors, true), false);

  await Swal.fire({
    icon: "warning",
    title: t("Cannot save yet"),
    // `text`, not `html`: a validation message can carry a value the user typed.
    text: messages.join("\n"),
    onOpen: dialogTestIds("validation-blocked"),
  });
  return false;
};

export const validate = (getValidationSchema) => {
  return (values) => {
    const validationSchema = getValidationSchema(values);
    try {
      validationSchema.validateSync(values, { abortEarly: false });
      return {};
    } catch (error) {
      // console.dir(error);
      return getErrorsFromValidationError(error);
    }
  };
};

export const getErrorsFromValidationError = (validationError) => {
  const FIRST_ERROR = 0;
  return validationError.inner?.reduce((errors, error) => {
    return {
      ...errors,
      [error.path]: error.errors[FIRST_ERROR],
    };
  }, {});
};

export const cleanFormData = (form) => {
  Object.keys(form).forEach(
    (key) =>
      (form[key] === undefined ||
        form[key] === null ||
        form[key] === "" ||
        form[key] === "Invalid date") &&
      delete form[key]
  );
  return form;
};

/** below is utils used in transaction creation and update*/

export const updateTransactionTotalValue = (
  dataM,
  { setFieldValue, values, delivery }
) => {
  var delivery = Number(delivery || (values?.delivery ?? 0));
  if (dataM == undefined || (dataM && dataM.length < 1)) {
    setFieldValue("value", delivery);
    setFieldValue("originValue", delivery);
    return;
  }

  var total = dataM
    ?.map((row) => row.subTotal)
    ?.reduce((subTotal, curr) => Number(curr) + Number(subTotal));
  setFieldValue("value", twoDecimal(Number(total) + delivery));
  setFieldValue("originValue", twoDecimal(Number(total) + delivery));
  setFieldValue(
    "change",
    twoDecimal(Number(values?.cash ?? 0) - (Number(total) + delivery))
  );
};

export const handleDeleteRow = async (
  index,
  { setFieldValue, values, invFeId }
) => {
  let newData = [...values?.dataM];
  // console.log(invFeId);
  if (invFeId) {
    index = newData?.findIndex((e) => e.invFeId == invFeId);
  }

  let dataToRemove = newData[index];
  // console.log(`index= ${index}`);
  // console.log(newData);
  if (index > -1) {
    // only splice array when item is found
    newData.splice(index, 1); // 2nd parameter means remove one item only
  }
  // console.log(newData);
  if (!!!dataToRemove?.invGroupId) {
    newData = newData.filter((item) => {
      // console.log(
      //   `${item?.invGroupId} | ${dataToRemove.invFeId} = ${
      //     item?.invGroupId != dataToRemove.invFeId
      //   }`
      // );
      return !!!item?.invGroupId || item?.invGroupId != dataToRemove.invFeId;
    });
  }

  setFieldValue("dataM", newData);
  updateTransactionTotalValue(newData, { setFieldValue, values });
};

export const newLine = ({ type, editableName }) => {
  return {
    invFeId: uuid(),
    sku: "-",
    name: type ?? "",
    description: "-",
    discount: 0,
    discountType: null,
    priceBuyPerUnit: 0,
    priceSellPerUnit: 0,
    stockToCount: 1,
    stockToCountProduct: 1,
    subTotal: 0,
    editableName: editableName,
  };
};

export const addNewLine = ({
  setFieldValue,
  values,
  index,
  type,
  editableName = true,
}) => {
  setFieldValue("dataM", [...values?.dataM, newLine({ type, editableName })]);
  return true;
};

export const handleTrxTypeChange = ({ values, setFieldValue }) => {
  let newData = values?.dataM?.map((product) => {
    const priceUnit = values?.trxType
      ? product?.priceBuyPerUnit
      : product?.priceSellPerUnit;
    product.subTotal = priceUnit * product.stockToCount;
    return product;
  });

  setFieldValue("dataM", newData);
  updateTransactionTotalValue(newData, { setFieldValue, values });
};

export const sorter = (e, { setSortBy, setIsAsc }) => {
  if (setSortBy != null) {
    if (e.column === "billStatus") {
      setSortBy("status");
    } else if (e.column === "isActive") {
      setSortBy("active");
    } else if (e.column) {
      setSortBy(e.column);
    }
  }

  if (setIsAsc == null) return;
  setIsAsc(e.asc);
};

export const sorterConfig = { external: true, resetable: false };

/** below use for product extraction */

export const extractProductOptions = (responseToParse) =>
  responseToParse?.map((eachData) => {
    return mapProduct(eachData);
  });

export const mapProduct = (eachData) => {
  return {
    value: eachData?.invProductId,
    label: (
      <>
        <label className="pb-0 mb-0">{eachData?.name}</label>
        <br />
        <small className="picker-meta pb-0 mb-0">
          {eachData?.brand ? eachData?.brand : ""}
        </small>
        <small className="picker-meta pb-0 mb-0">
          {eachData?.model?.label
            ? eachData?.brand
              ? " " + eachData?.model?.label
              : eachData?.model?.label
            : ""}
        </small>
        <small className="picker-meta pb-0 mb-0">
          {eachData?.category ? " " + eachData?.category : ""}
        </small>
        <p className="picker-meta pb-0 mb-0">
          <small>{eachData?.sku ?? ""}</small>
        </p>
      </>
    ),
    InvProductLogId: eachData?.invProductLogId,
    paid: eachData?.paid,
    sku: eachData?.sku,
    name: eachData?.name,
    moq: eachData?.moq,
    description: eachData?.description,
    priceBuyPerUnit: eachData?.priceBuyPerUnit,
    stockToCount: eachData?.stockToCount,
    // Whether the item has a shelf, carried from the catalogue onto the line that names it. A
    // line that does not say is treated as tracked by the server, so omitting it can only ever
    // preserve today's behaviour -- never silently stop stock from moving.
    trackQuantity: eachData?.trackQuantity,
    priceSellPerUnit: eachData?.priceSellPerUnit,
    partnerId: eachData?.partnerId,
    printerId: eachData?.printerId,
    printerName: eachData?.printerName,
    unPaidQuantity: eachData?.unPaidQuantity,
  };
};

export const handleStockChange = (
  index,
  key,
  data,
  { setFieldValue, values, invFeId, label }
) => {
  let newData = [...values?.dataM];

  if (invFeId) {
    index = newData?.findIndex((e) => e.invFeId == invFeId);
  }

  if (["stockToCountProduct"].includes(key)) {
    newData[index][key] = Number(data ?? 0);
  } else {
    newData[index][key] = data;
  }
  if (
    [
      "priceSellPerUnit",
      "priceBuyPerUnit",
      "stockToCount",
      "discount",
      "discountType",
    ].includes(key)
  ) {
    const priceUnit = values?.trxType
      ? newData[index]["priceSellPerUnit"]
      : newData[index]["priceBuyPerUnit"];
    var subTotal = twoDecimal(priceUnit * newData[index]["stockToCount"]);

    if (
      newData[index]["discountType"] == "percent" &&
      newData[index]["discount"]
    ) {
      subTotal = (subTotal * (100 - newData[index]["discount"] ?? 0)) / 100;
    } else if (
      newData[index]["discountType"] == "flat" &&
      newData[index]["discount"]
    ) {
      subTotal = subTotal - newData[index]["discount"] ?? 0;
    }

    newData[index]["subTotal"] = subTotal;
  }

  if (["salePersonId"].includes(key)) {
    newData[index]["salePersonName"] = label;
  }

  setFieldValue("dataM", newData);
  updateTransactionTotalValue(newData, { setFieldValue, values });
};

/**Partner helper */

const supplierBalance = (eachData) => {
  const billed = (eachData?.spend ?? 0) + (eachData?.spendExpenses ?? 0);
  const paid = eachData?.cash ?? 0;

  return (
    <p className="pb-0 mb-0">
      <small className="picker-meta" data-testid="partner-option-spend">
        total purchase: RM{formatDouble(eachData?.spend ?? 0)}
      </small>
      <br />
      <small
        className="picker-meta"
        data-testid="partner-option-spend-expenses"
      >
        total expenses: RM{formatDouble(eachData?.spendExpenses ?? 0)}
      </small>
      <br />
      <small className="picker-meta" data-testid="partner-option-balance">
        {paid > billed ? "extra payment: RM" : "total pending payment: RM"}
        {formatDouble(Math.abs(billed - paid))}
      </small>
    </p>
  );
};

export const extractPartnerOptions = (responseToParse) =>
  responseToParse?.map((eachData) => {
    return {
      value: eachData?.partnerId,
      /*
       * Plain-text identity beside the rich `label`, which is a multi-line JSX block carrying
       * phone, address, company and running totals.
       *
       * That block is right for the MENU, where an operator is choosing between two people
       * with similar names. It is wrong as the SELECTED value -- react-select renders the same
       * node in both places, so a picked customer displayed as four lines ending in
       * "total sales: RM800.00". Anything that needs to show the choice back to the user needs
       * a name; anything that needs the rest of the record needs `raw`.
       *
       * Additive: nothing that reads these options today looks at either field.
       */
      name:
        eachData?.name?.trim() || eachData?.companyName?.trim() || undefined,
      raw: eachData,
      label: (
        <label className="pb-0 mb-0">
          {eachData?.name?.trim()
            ? eachData?.name?.trim()
            : eachData?.companyName?.trim()}
          <small>
            {eachData?.telNo?.trim() ? " " + eachData?.telNo?.trim() : ""}
          </small>
          <small>
            {" "}
            {eachData?.email?.trim() ? " " + eachData?.email?.trim() : ""}
          </small>
          <br />
          <small className="picker-meta pb-0 mb-0">
            {eachData?.address?.trim()}
          </small>
          {eachData?.name?.trim() ? (
            <p className="pb-0 mb-0">
              <small className="picker-meta">
                {eachData?.companyName?.trim()}
              </small>
            </p>
          ) : (
            ""
          )}
          {eachData?.type == "CUSTOMER" ? (
            <p className="pb-0 mb-0">
              <small className="picker-meta" data-testid="partner-option-spend">
                total sales: RM{formatDouble(eachData?.spend ?? 0)}
              </small>
              <br />
              <small
                className="picker-meta"
                data-testid="partner-option-balance"
              >
                {(eachData?.cash ?? 0) > (eachData?.spend ?? 0)
                  ? "extra payment: RM"
                  : "total pending payment: RM"}
                {formatDouble(
                  Math.abs((eachData?.spend ?? 0) - (eachData?.cash ?? 0))
                )}
              </small>
            </p>
          ) : eachData?.type == "SUPPLIER" ? (
            // A supplier's `cash` covers payments against purchases AND expenses, while `spend`
            // is purchases alone -- weighing the two against each other reported every settled
            // expense as an overpayment.
            supplierBalance(eachData)
          ) : (
            ""
          )}
        </label>
      ),
    };
  });

export const extractStaffOptions = (responseToParse, configType) =>
  responseToParse?.map((eachData) => {
    return {
      value: eachData?.partnerId,
      label: eachData?.name,
    };
  });

export const extractGroupOptions = (data) =>
  data
    ? data.map((group) => {
        return {
          value: group.partnerGroupId,
          label: group.name,
          partnerGroupId: group.partnerGroupId,
        };
      })
    : data;

export const setInitialProductList = (products, trxType) => {
  if (!products) return [];
  return products.map((product, index) => {
    var subTotal = trxType
      ? twoDecimal(product?.stockToCount * product?.priceSellPerUnit)
      : twoDecimal(product?.stockToCount * product?.priceBuyPerUnit);

    if (product.discountType == "percent") {
      subTotal =
        (Number(subTotal) * (100 - Number(product.discount ?? 0))) / 100;
    } else {
      subTotal = Number(subTotal) - Number(product.discount ?? 0);
    }

    return {
      invProductLogId: product?.invProductLogId,
      paid: product?.paid,
      invProductId: product?.invProductId,
      sku: product?.sku,
      name: product?.name,
      description: product?.description,
      discount: product?.discount ?? 0,
      discountType: product?.discountType,
      editableName: product?.editableName,
      stockToCount: product?.stockToCount,
      stockToCountProduct: product?.stockToCountProduct,
      // Read back from the LINE the bill stored, not from the catalogue as it stands now -- the
      // server computes both ends of an edit from the line for the same reason.
      trackQuantity: product?.trackQuantity,
      priceSellPerUnit: product?.priceSellPerUnit,
      priceBuyPerUnit: product?.priceBuyPerUnit,
      invGroupId: product?.invGroupId,
      /*
       * A STABLE front-end id, never a fresh one per call.
       *
       * This list feeds `initialValues` on every screen that shows bill lines, and those forms
       * run with `enableReinitialize` -- so a `uuid()` here made initialValues differ on every
       * render and Formik reset the form after every keystroke. On the till that meant the cash
       * a cashier typed snapped back to 0 and the screen then asked for it again in a dialog,
       * having already decided the invoice was worth nothing.
       *
       * Lines saved through the portal carry their own `invFeId`, and an add-on's `invGroupId`
       * points at its parent's -- so a value that is present must never be recomputed. Lines the
       * server generated, from a waiter order say, have none; their log id is stable, unique,
       * and was never referenced by anything, so nothing can be pointing at the id they lacked.
       */
      invFeId: product?.invFeId ?? product?.invProductLogId ?? `row-${index}`,
      salePersonName: product?.salePersonName,
      salePersonId: product?.salePersonId,
      subTotal: subTotal,
      printerId: product?.printerId,
      printerName: product?.printerName,
      unPaidQuantity: product?.unPaidQuantity,
    };
  });
};
export const FeedBackInput = ({
  invalid,
  error,
  name,
  testId: testIdOverride,
}) => {
  return invalid && error ? (
    <label
      className="form-label"
      style={{ color: "red" }}
      {...testId("field-error", testIdOverride, name)}
    >
      {error}
    </label>
  ) : (
    <></>
  );
};

export const extractPrinterOption = (responseToParse) =>
  responseToParse?.map((eachData) => {
    return mapPrinter(eachData);
  });

export const mapPrinter = (printer) => {
  return {
    value: printer?.settingId,
    label: printer?.name,
  };
};

export const extractModelOption = (responseToParse) =>
  responseToParse?.map((eachData) => {
    return mapModel(eachData);
  });

export const mapModel = (model) => {
  return {
    value: model?.keyValueId,
    label: model?.label,
  };
};

export const roundingAdjustment = (total, cashIn) => {
  var balance = Number(cashIn ?? 0) - Number(total ?? 0);

  var result = twoDecimal(balance);
  // if (result < 0) {
  //   return 0;
  // }
  return result;
};

export const handleBalance = (cashIn, values, setFieldValue) => {
  cashIn = Number(cashIn ?? 0);

  if (cashIn && cashIn > 0 && values?.value && Number(values?.value ?? 0) > 0) {
    setFieldValue("change", roundingAdjustment(values.value, cashIn));
    setFieldValue("partialBalance", roundingAdjustment(cashIn, values.value));
    if (Number(cashIn ?? 0) < Number(values.value ?? 0)) {
      setFieldValue("billStatus", "PARTIAL");
    } else {
      setFieldValue("billStatus", "PAID");
    }
  }
};

export const twoDecimal = (val) => {
  if (!val) return 0.0;

  return Number(val ?? 0.0).toFixed(2);
};

export const tabActive = (history, defaultTab) => {
  return history?.location?.hash?.replace("#", "") == ""
    ? defaultTab
    : history?.location?.hash?.replace("#", "");
};

export const extractPartnerCodeOptions = (responseToParse) =>
  responseToParse?.map((eachData) => {
    return {
      value: eachData?.partnerId,
      label:
        eachData?.name +
        (eachData?.telNo ? " | " + eachData?.telNo : "") +
        (eachData?.email ? " | " + eachData?.email : ""),
      raw: eachData,
    };
  });

export const InvoiceTitle = (configType = "", invoiceType = "") => {
  const { t } = useTranslation();
  if (
    configType.includes(
      "OTHERSarrREFUNDarrCASHOUTarrCAPITALSpAcEEQUITYarrCAPITALSpAcEDEPTHarrPURCHASEarrEXPENSES"
    )
  )
    return t("Purchase & Expenses");
  if (invoiceType.includes("ORDER") && configType.includes("SALE"))
    return t("Sale Order");
  if (invoiceType.includes("ORDER") && configType.includes("PURCHASE"))
    return t("Purchase Order");

  if (invoiceType.includes("QUOTATION")) return t("Quotation");

  if (configType == "SALESarrINCOMESpAcEOTHER") return t("Sale / Other Income");
  if (configType == "SALES" || configType == "SALE") return t("Sale");

  if (
    configType.includes("INCOMESpAcEOTHER") ||
    (configType.includes("CAPITAL DEPTH".replaceAll(" ", "SpAcE")) &&
      !configType.includes("CAPITAL DEPTH CREDIT".replaceAll(" ", "SpAcE")))
  )
    return t("Other Income");

  if (configType.includes("PURCHASE")) return t("Purchase");
  if (configType == "EXPENSES") return t("Expenses");
  if (configType == "OTHERS") return t("Non-deductable expenses");
  if (configType == "CAPITAL EQUITY".replaceAll(" ", "SpAcE"))
    return t("Capital");
  if (configType == "CAPITAL DEPTH".replaceAll(" ", "SpAcE")) return t("Loan");
  if (configType == "CAPITAL DEPTH CREDIT".replaceAll(" ", "SpAcE"))
    return t("Loan - credit");
  if (configType == "CASHOUT") return t("Dividen Payment");
  // Kept, and labelled for what it is. Existing REFUND transactions must stay readable and
  // recognisable; only the offer of the category for something NEW was retired. A refund raised
  // today is a Refund Note against the invoice it relates to.
  if (configType == "REFUND") return t("Refund (legacy)");
  if (configType == "ASSET FIXED".replaceAll(" ", "SpAcE"))
    return t("Fixed asset");
  if (configType == "ASSET CURRENT".replaceAll(" ", "SpAcE"))
    return t("Current asset");
  if (configType == "TRANSFER DEBIT".replaceAll(" ", "SpAcE"))
    return t("Transfer debit");
  if (configType == "TRANSFER CREDIT".replaceAll(" ", "SpAcE"))
    return t("Transfer credit");

  return "Refund / Non-deductable expenses / Other expenses";
};

export const CustomerType = (configType = "", invoiceType = "") => {
  if (invoiceType.includes("ORDER") && configType.includes("SALE"))
    return "CUSTOMER";
  if (invoiceType.includes("ORDER") && configType.includes("PURCHASE"))
    return "SUPPLIER";

  if (invoiceType.includes("QUOTATION")) return "CUSTOMER";

  if (configType.includes("SALE") || configType.includes("INCOME OTHER"))
    return "CUSTOMER";
  if (configType.includes("PURCHASE")) return "SUPPLIER";

  if (
    configType.includes(
      "INCOMESpAcEOTHERarrCAPITALSpAcEDEPTHarrCAPITALSpAcEEQUITYarrCAPITALSpAcEEQUITYarrCAPITALSpAcEDEPTHSpAcECREDITSpAcEREPAYMENT"
    )
  )
    return "CUSTOMER";

  return "SUPPLIER";
};

// Copies a string to the clipboard. Must be called from within an
// event handler such as click. May return false if it failed, but
// this is not always possible. Browser support for Chrome 43+,
// Firefox 42+, Safari 10+, Edge and Internet Explorer 10+.
// Internet Explorer: The clipboard feature may be disabled by
// an administrator. By default a prompt is shown the first
// time the clipboard is used (per session).
export const copyToClipboard = (window, text) => {
  if (window.clipboardData && window.clipboardData.setData) {
    // Internet Explorer-specific code path to prevent textarea being shown while dialog is visible.
    return window.clipboardData.setData("Text", text);
  } else if (
    document.queryCommandSupported &&
    document.queryCommandSupported("copy")
  ) {
    var textarea = document.createElement("textarea");
    textarea.textContent = text;
    textarea.style.position = "fixed"; // Prevent scrolling to bottom of page in Microsoft Edge.
    document.body.appendChild(textarea);
    textarea.select();
    try {
      return document.execCommand("copy"); // Security exception may be thrown by some browsers.
    } catch (ex) {
      console.warn("Copy to clipboard failed.", ex);
      return prompt("Copy to clipboard: Ctrl+C, Enter", text);
    } finally {
      document.body.removeChild(textarea);
    }
  }
};

export const setHtmlTitle = (title) => {
  const el = document.querySelector("title");
  el.innerText = title;
};

export const setHtmlDescription = (type = "description", desc) => {
  const el = document.querySelector(`meta[name="${type}"]`);
  el.setAttribute("content", desc);
};
